import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, GraduationCap, Code2, Layers, ChevronRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Home = () => {
    const { t, language } = useLanguage();
    const [stats, setStats] = useState({ experiences: 0, applications: 0, services: 0 });
    const [presentation, setPresentation] = useState({
        titre_fr: 'Ingénieur Informatique — Business Analyst Senior',
        titre_en: 'IT Engineer — Senior Business Analyst',
        texte_fr: 'Plus de 15 ans d\'expertise en analyse métier, développement et gestion de projets SI pour des grands comptes internationaux.',
        texte_en: 'Over 15 years of expertise in business analysis, development and IT project management for major international companies.',
        annees_experience: 15
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [exp, apps, services, pres] = await Promise.all([
                    axios.get(`${API}/experiences/public`),
                    axios.get(`${API}/applications/public`),
                    axios.get(`${API}/services/public`),
                    axios.get(`${API}/presentation`)
                ]);
                setStats({
                    experiences: exp.data.length,
                    applications: apps.data.length,
                    services: services.data.length
                });
                if (pres.data) {
                    setPresentation(pres.data);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, []);

    const highlights = [
        {
            icon: <Briefcase className="w-6 h-6" />,
            value: `${presentation.annees_experience}+`,
            label: t('Années d\'expérience', 'Years of experience'),
            color: 'blue'
        },
        {
            icon: <GraduationCap className="w-6 h-6" />,
            value: t('Ingénieur', 'Engineer'),
            label: t('Diplôme Informatique', 'Computer Science'),
            color: 'violet'
        },
        {
            icon: <Code2 className="w-6 h-6" />,
            value: '25+',
            label: t('Projets réalisés', 'Completed projects'),
            color: 'cyan'
        },
        {
            icon: <Layers className="w-6 h-6" />,
            value: t('Agile', 'Agile'),
            label: t('Méthodologie', 'Methodology'),
            color: 'blue'
        },
        {
            icon: <Sparkles className="w-6 h-6" />,
            value: t('Intelligence Artificielle', 'Artificial Intelligence'),
            label: t('Prompt Engineering', 'Prompt Engineering'),
            color: 'violet'
        }
    ];

    const expertise = [
        {
            title: t('Banque & Finance', 'Banking & Finance'),
            description: t('SEPA, virements internationaux, DSP2, Open Banking', 'SEPA, international transfers, DSP2, Open Banking'),
            clients: ['Société Générale', 'Oney+']
        },
        {
            title: t('Retail & Distribution', 'Retail & Distribution'),
            description: t('MDM, PIM, gestion des données produits', 'MDM, PIM, product data management'),
            clients: ['Carrefour', 'Darty']
        },
        {
            title: t('Télécom & Média', 'Telecom & Media'),
            description: t('VOD, applications B2B, Web Services', 'VOD, B2B applications, Web Services'),
            clients: ['Orange', 'Canal+']
        }
    ];

    return (
        <main data-testid="home-page" className="min-h-screen">
            {/* Hero Section */}
            <section className="hero-bg min-h-screen flex items-center pt-20" data-testid="hero-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                        {/* Left Content */}
                        <div className="stagger-children">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm mb-8">
                                <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
                                {t('Disponible pour de nouveaux projets', 'Available for new projects')}
                            </div>
                            
                            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
                                <span className="text-white">Djiby</span>{' '}
                                <span className="text-gradient">NDIAYE</span>
                            </h1>
                            
                            <p className="text-xl md:text-2xl text-slate-300 font-medium mb-4">
                                {language === 'en' ? presentation.titre_en : presentation.titre_fr}
                            </p>
                            
                            <p className="text-lg text-slate-400 mb-6 max-w-xl">
                                {language === 'en' ? presentation.texte_en : presentation.texte_fr}
                            </p>

                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact">
                                    <Button 
                                        size="lg" 
                                        className="btn-glow bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8"
                                        data-testid="cta-contact"
                                    >
                                        {t('Me contacter', 'Contact me')}
                                        <ArrowRight className="ml-2 w-5 h-5" />
                                    </Button>
                                </Link>
                                <Link to="/experiences">
                                    <Button 
                                        size="lg" 
                                        variant="outline" 
                                        className="rounded-full px-8 border-white/20 text-white hover:bg-white/10"
                                        data-testid="cta-experiences"
                                    >
                                        {t('Voir mon parcours', 'View my journey')}
                                    </Button>
                                </Link>
                            </div>
                        </div>

                        {/* Right - Stats Cards */}
                        <div className="grid grid-cols-2 gap-2 stagger-children">
                            {highlights.map((item, index) => (
                                <div 
                                    key={index}
                                    className={`glass-card p-3 card-hover ${index === 0 ? 'col-span-2' : ''}`}
                                    data-testid={`highlight-${index}`}
                                >
                                    <div className={`w-9 h-9 rounded-xl bg-${item.color}-500/20 flex items-center justify-center text-${item.color}-400 mb-2`}>
                                        {item.icon}
                                    </div>
                                    <div className="text-xl font-bold text-white mb-0.5">{item.value}</div>
                                    <div className="text-slate-400 text-xs">{item.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Expertise Section */}
            <section className="py-8 md:py-12" data-testid="expertise-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-4">
                            {t('Domaines d\'expertise', 'Areas of expertise')}
                        </h2>
                        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                            {t(
                                'Une expérience solide dans des secteurs exigeants',
                                'Solid experience in demanding sectors'
                            )}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {expertise.map((item, index) => (
                            <div 
                                key={index}
                                className="glass-card p-8 card-hover group"
                                data-testid={`expertise-${index}`}
                            >
                                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-blue-400 transition-colors">
                                    {item.title}
                                </h3>
                                <p className="text-slate-400 mb-6">
                                    {item.description}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {item.clients.map((client, i) => (
                                        <span key={i} className="tech-badge">
                                            {client}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 md:py-24" data-testid="cta-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="glass-card p-12 md:p-16 text-center relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-violet-500/10"></div>
                        <div className="relative z-10">
                            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
                                {t('Un projet en tête ?', 'Have a project in mind?')}
                            </h2>
                            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
                                {t(
                                    'Discutons de vos besoins et voyons comment je peux vous accompagner dans vos projets SI.',
                                    'Let\'s discuss your needs and see how I can support you in your IT projects.'
                                )}
                            </p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <Link to="/services">
                                    <Button 
                                        size="lg"
                                        variant="outline"
                                        className="rounded-full px-8 border-white/20 text-white hover:bg-white/10"
                                        data-testid="cta-services"
                                    >
                                        {t('Découvrir mes services', 'Discover my services')}
                                        <ChevronRight className="ml-2 w-5 h-5" />
                                    </Button>
                                </Link>
                                <Link to="/contact">
                                    <Button 
                                        size="lg"
                                        className="btn-glow bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8"
                                        data-testid="cta-contact-2"
                                    >
                                        {t('Demander un devis', 'Request a quote')}
                                        <ArrowRight className="ml-2 w-5 h-5" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};
